import { useRef, useState, useCallback, useEffect } from 'react';
import { Eraser, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { uploadFile } from '@/lib/api/upload';

interface InlineSignaturePadProps {
  onSign: (signatureUrl: string) => void;
  disabled?: boolean;
  sessionToken?: string;
}

/**
 * Convert a canvas data URL to a File object for upload.
 */
function dataUrlToFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',');
  const mime = arr[0]?.match(/:(.*?);/)?.[1] ?? 'image/png';
  const bstr = atob(arr[1] ?? '');
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

/**
 * Inline signature capture component for chat interface.
 * Provides a canvas-based drawing pad for signatures.
 * Uploads signature to R2 storage on submit.
 */
export function InlineSignaturePad({
  onSign,
  disabled = false,
  sessionToken,
}: InlineSignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize canvas context
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getCoordinates = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      if ('touches' in e) {
        const touch = e.touches[0];
        if (!touch) return null;
        return {
          x: (touch.clientX - rect.left) * scaleX,
          y: (touch.clientY - rect.top) * scaleY,
        };
      }

      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    },
    []
  );

  const startDrawing = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled || isUploading) return;

      const coords = getCoordinates(e);
      if (!coords) return;

      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;

      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
      setIsDrawing(true);
      setHasSignature(true);
    },
    [disabled, isUploading, getCoordinates]
  );

  const draw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing || disabled || isUploading) return;

      const coords = getCoordinates(e);
      if (!coords) return;

      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;

      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
    },
    [isDrawing, disabled, isUploading, getCoordinates]
  );

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature) return;

    setIsUploading(true);
    setError(null);

    try {
      const dataUrl = canvas.toDataURL('image/png');
      const filename = `signature-${Date.now()}.png`;
      const file = dataUrlToFile(dataUrl, filename);

      const metadata = await uploadFile(file, { sessionToken });
      onSign(metadata.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload signature');
    } finally {
      setIsUploading(false);
    }
  }, [hasSignature, onSign, sessionToken]);

  const isDisabled = disabled || isUploading;

  return (
    <div className="flex flex-col gap-3">
      <div
        className={cn(
          'relative rounded-lg border-2 border-dashed border-border bg-white',
          'overflow-hidden',
          isDisabled && 'opacity-50'
        )}
      >
        <canvas
          ref={canvasRef}
          width={300}
          height={150}
          className={cn(
            'w-full touch-none',
            isDisabled ? 'cursor-not-allowed' : 'cursor-crosshair'
          )}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />

        {!hasSignature && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="text-sm text-muted-foreground">
              Draw your signature here
            </span>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClear}
          disabled={isDisabled || !hasSignature}
          className="gap-1"
        >
          <Eraser className="size-4" />
          Clear
        </Button>

        <Button
          type="button"
          size="sm"
          onClick={handleSubmit}
          disabled={isDisabled || !hasSignature}
          className="gap-1"
        >
          {isUploading ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Uploading...
            </>
          ) : (
            'Submit Signature'
          )}
        </Button>
      </div>
    </div>
  );
}
